// ADR-0153 (fase 3, corte 1, #0750) — lógica pura del panel de encuestas
// del proyecto: los textos de las tiles, la distribución y los tonos. Sin
// DOM ni red. Un número sólo con 5 respuestas o más (el servidor manda
// null con menos): acá se pinta «Pocas respuestas», nunca un número.
//
// El promedio por contratista es una evaluación de un tercero (ADR-0153,
// ROPA): sólo se muestra al gerente y al jefe; nunca ordena ni asigna
// trabajo.
import type { PostventaNpsWaveSummary } from "@bitacorapro/api-types";
import { OWNER_SURVEY_MIN_N, type OwnerSurveyKind } from "@bitacorapro/core-engine";

export const POCAS_RESPUESTAS = "Pocas respuestas";

/** «2 meses», «9 meses», «Visita técnica». */
export const SURVEY_KIND_LABEL: Readonly<Record<OwnerSurveyKind, string>> = {
  nps_2m: "NPS a los 2 meses",
  nps_9m: "NPS a los 9 meses",
  visita: "Visita técnica",
};

/** El valor de la tile del NPS: «+40», «0», «−15» o «—» con pocas. */
export function npsValor(nps: number | null): string {
  if (nps === null) return "—";
  if (nps > 0) return `+${nps}`;
  if (nps < 0) return `−${Math.abs(nps)}`;
  return "0";
}

/** «4,2» con un decimal (es-CL), o «—». */
export function promedioValor(p: number | null): string {
  return p === null ? "—" : p.toLocaleString("es-CL", { minimumFractionDigits: 1 });
}

/** La línea de apoyo de una ola: «Pocas respuestas · 3 de 10» o «12 de 30
 *  respondieron». */
export function respuestasHint(respondidas: number, enviadas: number): string {
  const base = `${respondidas} de ${enviadas} respondieron`;
  return respondidas < OWNER_SURVEY_MIN_N && enviadas > 0
    ? `${POCAS_RESPUESTAS} · ${base}`
    : enviadas === 0
      ? "Sin encuestas enviadas"
      : base;
}

/** Tasa de respuesta en %, o null sin envíos. */
export function tasaRespuesta(respondidas: number, enviadas: number): number | null {
  return enviadas === 0 ? null : Math.round((100 * respondidas) / enviadas);
}

/** Las dos olas juntas para la tasa de respuesta general. */
export function sumarOlas(a: PostventaNpsWaveSummary, b: PostventaNpsWaveSummary) {
  return { respondidas: a.respondidas + b.respondidas, enviadas: a.enviadas + b.enviadas };
}

export type BarTone = "crit" | "warn" | "ok";

/** El tono de cada barra de la distribución NPS: 0–6 detractor, 7–8
 *  pasivo, 9–10 promotor. */
export function npsTone(score: number): BarTone {
  if (score >= 9) return "ok";
  if (score >= 7) return "warn";
  return "crit";
}

/** El tono de la visita (1–5): 1–2 bajo, 3 medio, 4–5 alto. */
export function visitaTone(score: number): BarTone {
  if (score >= 4) return "ok";
  if (score === 3) return "warn";
  return "crit";
}

/** Las filas de una distribución: el puntaje, su cantidad y el ancho de
 *  la barra relativo a la más alta. */
export function distribucion(
  dist: readonly number[],
  first: number
): Array<{ score: number; n: number; pct: number }> {
  const max = Math.max(0, ...dist);
  return dist.map((n, i) => ({
    score: first + i,
    n,
    pct: max === 0 ? 0 : Math.round((100 * n) / max),
  }));
}

/** ¿Hay respuestas suficientes para mostrar una distribución? Con menos
 *  de 5, las barras y los conteos revelarían el número que la tile oculta
 *  (con 3 promotores se lee un NPS de +100) y, en la visita de un solo
 *  contratista, su promedio exacto (decisión 9 del founder). */
export function distribucionVisible(respondidas: number): boolean {
  return respondidas >= OWNER_SURVEY_MIN_N;
}

function contar(n: number, singular: string, plural: string): string {
  return `${n} ${n === 1 ? singular : plural}`;
}

/** «3 promotores · 1 pasivo · 0 detractores». */
export function bucketsTexto(
  ola: Pick<PostventaNpsWaveSummary, "promotores" | "pasivos" | "detractores">
): string {
  return [
    contar(ola.promotores, "promotor", "promotores"),
    contar(ola.pasivos, "pasivo", "pasivos"),
    contar(ola.detractores, "detractor", "detractores"),
  ].join(" · ");
}

/** La fecha de una respuesta en la tabla: la del comentario si lo hay
 *  (puede llegar días después del número), si no la del número. Es la
 *  misma fecha por la que el servidor ordena. */
export function fechaRespuesta(r: { respondedAt: string; commentedAt: string | null }): string {
  return r.commentedAt ?? r.respondedAt;
}

/** El rótulo del total de la tabla: «12» o «200 más recientes de 340». */
export function totalRespuestasTexto(mostradas: number, total: number): string {
  return total > mostradas ? `${mostradas} más recientes de ${total}` : String(total);
}
